/**
 * ArticleGrid — grid of ArticleCards with empty-state fallback
 */
import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { ArticleCard } from "./ArticleCard";

interface Article {
  id: string;
  handle: string;
  title: string;
  excerpt?: string | null;
  publishedAt: string;
  image?: { url: string; altText?: string | null } | null;
  authorV2?: { name?: string | null } | null;
  blog: { handle: string };
}

interface ArticleGridProps {
  articles: Array<Article>;
  locale: string;
  headingLevel?: "h2" | "h3";
}

export function ArticleGrid({
  articles,
  locale,
  headingLevel,
}: Readonly<ArticleGridProps>) {
  const { t } = useTranslation();

  if (articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl bg-background-7 py-20 text-center">
        <BookOpen
          className="mb-4 size-10 text-text-4"
          strokeWidth={1.5}
          aria-hidden
        />
        <h2 className="mb-1 text-medium font-bold text-text-1">
          {t("Blog.emptyTitle")}
        </h2>
        <p className="text-small text-text-3">{t("Blog.emptySubtitle")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((article) => (
        <ArticleCard
          key={article.id}
          article={article}
          locale={locale}
          headingLevel={headingLevel}
        />
      ))}
    </div>
  );
}
